import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { CATEGORIES, getProjectBySlug, PROJECTS } from "../data/projects";
import { Lightbox } from "../components/Lightbox";
import { ArrowLeft, ArrowRight, Play, ExternalLink, X } from "lucide-react";

const ProcessRow = ({ items, onOpen, baseIndex }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
    {items.map((item, i) => (
      <figure key={i} className="flex flex-col">
        <button
          type="button"
          onClick={() => onOpen(baseIndex + i)}
          className="group relative w-full aspect-square rounded-2xl overflow-hidden bg-black/5"
          data-testid={`process-image-${i + 1}`}
          aria-label={`Open ${item.label} fullscreen`}
        >
          <img
            src={item.image}
            alt={item.label}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            loading="lazy"
            draggable={false}
          />
        </button>
        <figcaption className={`mt-3 flex items-baseline justify-between gap-3 ${!item.label && !item.note ? "hidden" : ""}`}>
          {item.label && (
            <span className="font-body font-bold text-white text-sm md:text-base tracking-tight">
              {item.label}
            </span>
          )}
          {item.note && (
            <span className="font-body text-white/55 text-xs md:text-sm text-right max-w-[60%]">
              {item.note}
            </span>
          )}
        </figcaption>
      </figure>
    ))}
  </div>
);

const CampaignRow = ({ items, onOpen, baseIndex, lightboxImages }) => {
  // Map each campaign item back to its index in the lightbox image list (videos are skipped).
  const indexMap = items.map((item) =>
    item.type === "video" ? -1 : lightboxImages.indexOf(item.image)
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
      {items.map((item, i) => {
        const span = item.span === "half" ? "md:col-span-1" : "md:col-span-2";
        const lbIdx = indexMap[i];

        if (item.type === "video") {
          return (
            <figure key={i} className={span}>
              <div
                className="relative w-full rounded-2xl overflow-hidden bg-black aspect-square"
                data-testid={`campaign-video-${i + 1}`}
              >
                <video
                  src={item.src}
                  poster={item.poster}
                  controls
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              {item.label && (
                <figcaption className="mt-3 font-body text-white/65 text-xs md:text-sm tracking-[0.18em] uppercase">
                  {item.label}
                </figcaption>
              )}
            </figure>
          );
        }

        return (
          <figure key={i} className={span}>
            <button
              type="button"
              onClick={() => lbIdx >= 0 && onOpen(lbIdx)}
              className="group block w-full rounded-2xl overflow-hidden bg-black/5"
              data-testid={`campaign-image-${i + 1}`}
              aria-label={`Open ${item.label || "image"} fullscreen`}
            >
              <img
                src={item.image}
                alt={item.label || "Campaign asset"}
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                loading="lazy"
                draggable={false}
              />
            </button>
            {item.label && (
              <figcaption className="mt-3 font-body text-white/65 text-xs md:text-sm tracking-[0.18em] uppercase">
                {item.label}
              </figcaption>
            )}
          </figure>
        );
      })}
    </div>
  );
};

const PrototypeBlock = ({ prototype }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-12 md:mt-20" data-testid="prototype-section">
      <header className="mb-6 md:mb-10 flex items-baseline justify-between gap-4">
        <span className="text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px]">
          Prototype
        </span>
        <span className="h-px flex-1 bg-white/15" />
        <span className="text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px]">
          Interactive
        </span>
      </header>

      <div className="bg-[#FEFBEA] rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)]">
        {!open ? (
          <div className="px-6 md:px-14 py-14 md:py-24 flex flex-col items-center text-center gap-5">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-black text-white">
              <Play className="w-6 h-6 ml-0.5" />
            </span>
            <h3 className="font-body font-extrabold text-black tracking-tight text-2xl md:text-4xl">
              Try the live prototype
            </h3>
            <p className="max-w-md font-body font-medium text-black/65 text-sm md:text-base leading-relaxed">
              Explore the full Figma flow — from product browse to checkout — in
              an embedded interactive preview.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="open-prototype"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-black text-white font-body font-semibold text-sm tracking-wide shadow-[0_8px_24px_rgba(0,0,0,0.25)] hover:bg-[#E5983D] transition-colors"
              >
                <Play className="w-4 h-4" /> {prototype.label || "View Prototype"}
              </button>
              {prototype.externalUrl && (
                <a
                  href={prototype.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="open-prototype-external"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-black/15 text-black font-body font-semibold text-sm tracking-wide hover:bg-black hover:text-white transition-colors"
                >
                  <ExternalLink className="w-4 h-4" /> Open in Figma
                </a>
              )}
            </div>
          </div>
        ) : (
          <div className="p-3 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="font-body text-black/55 text-xs md:text-sm tracking-[0.18em] uppercase">
                Figma — Interactive Prototype
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="close-prototype"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/5 hover:bg-black/10 text-black text-xs font-body font-semibold tracking-wide transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Close
              </button>
            </div>
            <div className="relative w-full overflow-hidden rounded-2xl border border-black/10 bg-white" style={{ paddingTop: "56.25%" }}>
              <iframe
                title="Twice as Nice — Figma Prototype"
                src={prototype.embedUrl}
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="prototype-iframe"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = getProjectBySlug(slug);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  if (!project) {
    return (
      <section
        className="max-w-3xl mx-auto px-4 md:px-6 py-16 md:py-24 text-center"
        data-testid="project-not-found"
      >
        <p className="font-body text-white/80 text-lg">
          That project couldn&apos;t be found.
        </p>
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-full bg-white text-black font-body font-semibold text-sm shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
        >
          <ArrowLeft className="w-4 h-4" /> Back to projects
        </Link>
      </section>
    );
  }

  const categoryLabel =
    CATEGORIES.find((c) => c.id === project.category)?.label || project.category;

  // Build a flat lightbox sequence (hero + process + campaign + leftover gallery)
  const lightboxImages = project.images;

  const isStructured = Boolean(project.process || project.campaign);

  // Adjacent project (same category) for "Next project"
  const sameCategory = PROJECTS.filter((p) => p.category === project.category);
  const idxIn = sameCategory.findIndex((p) => p.slug === project.slug);
  const next = sameCategory[(idxIn + 1) % sameCategory.length];

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(-1);
  const prev = () =>
    setLightboxIndex(
      (i) => (i - 1 + lightboxImages.length) % lightboxImages.length
    );
  const nextImg = () =>
    setLightboxIndex((i) => (i + 1) % lightboxImages.length);

  // Indexes within lightboxImages
  const heroIdx = 0;
  const processStart = 1;
  const campaignStart = 1 + (project.process?.length || 0);

  // For non-structured (legacy) — split images into hero + gallery
  const [hero, ...rest] = lightboxImages;

  return (
    <section
      className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12"
      data-testid={`project-detail-${project.slug}`}
    >
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate("/projects")}
        data-testid="back-to-projects"
        className="inline-flex items-center gap-2 mb-6 md:mb-10 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-xs md:text-sm font-body font-semibold tracking-wide transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to projects
      </button>

      {/* Hero image */}
      <button
        type="button"
        onClick={() => openLightbox(heroIdx)}
        className="block w-full rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] group"
        aria-label="Open hero image fullscreen"
        data-testid="project-hero-button"
      >
        <img
          src={project.cover}
          alt={`${project.title} — hero`}
          className="w-full h-auto md:max-h-[88vh] object-contain bg-black transition-transform duration-700 group-hover:scale-[1.01]"
          draggable={false}
        />
      </button>

      {/* Split layout (e.g. Nike × Bratz) — text + gallery side by side */}
      {project.layout === "split" ? (
        <div className="mt-10 md:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Left — text/content/details */}
          <div className="flex flex-col gap-6 md:gap-8">
            {/* Optional inline video */}
            {project.campaign?.find((c) => c.type === "video") && (
              <div className="rounded-2xl overflow-hidden bg-black border border-white/10">
                <video
                  src={project.campaign.find((c) => c.type === "video").src}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-auto block"
                  data-testid="split-video"
                />
              </div>
            )}

            <div>
              <div className="flex items-center gap-3 text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px]">
                <span>{categoryLabel}</span>
                <span className="w-1 h-1 rounded-full bg-white/40" />
                <span>{project.year}</span>
              </div>
              <h1
                className="mt-3 font-body font-extrabold text-white tracking-tight text-4xl md:text-5xl leading-[1.05]"
                data-testid={`project-title-${project.slug}`}
              >
                {project.title}
              </h1>
              {project.role && (
                <p className="mt-3 font-body font-medium text-white/70 text-sm md:text-base leading-relaxed">
                  {project.role}
                </p>
              )}
            </div>

            <div
              className="font-body font-medium text-white/85 text-base md:text-lg leading-[1.85] space-y-5"
              data-testid={`project-description-${project.slug}`}
            >
              {project.description.split(/\n\n+/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>

          {/* Right — responsive gallery grid */}
          <div
            className="grid grid-cols-2 gap-3 md:gap-4 self-start"
            data-testid="split-gallery"
          >
            {lightboxImages
              .slice(1)
              .map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => openLightbox(i + 1)}
                  className="group block w-full rounded-2xl overflow-hidden bg-white/5 transition-transform duration-500 ease-out hover:-translate-y-1"
                  data-testid={`split-gallery-item-${i + 1}`}
                  aria-label={`Open image ${i + 2} fullscreen`}
                >
                  <img
                    src={src}
                    alt={`${project.title} — ${i + 2}`}
                    className="w-full h-auto block transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                    loading="lazy"
                    draggable={false}
                  />
                </button>
              ))}
          </div>
        </div>
      ) : (
        <>
      {/* Process section (structured projects only) */}
      {project.process?.length > 0 && (
        <div className="mt-12 md:mt-20">
          <header className="mb-6 md:mb-10 flex items-baseline justify-between gap-4">
            <span className="text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px]">
              Process
            </span>
            <span className="h-px flex-1 bg-white/15" />
            <span className="text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px]">
              {String(project.process.length).padStart(2, "0")} stages
            </span>
          </header>
          <ProcessRow
            items={project.process}
            onOpen={openLightbox}
            baseIndex={processStart}
          />
        </div>
      )}

      {/* Campaign / final designs */}
      {project.campaign?.length > 0 && (
        <div className="mt-12 md:mt-20">
          <header className="mb-6 md:mb-10 flex items-baseline justify-between gap-4">
            <span className="text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px]">
              Campaign
            </span>
            <span className="h-px flex-1 bg-white/15" />
            <span className="text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px]">
              Final Designs
            </span>
          </header>
          <CampaignRow
            items={project.campaign}
            onOpen={openLightbox}
            baseIndex={campaignStart}
            lightboxImages={lightboxImages}
          />
        </div>
      )}

      {/* Legacy gallery (only used by photography projects without process/campaign) */}
      {!isStructured && rest.length > 0 && (
        <div className="mt-4 md:mt-6 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
          {rest.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => openLightbox(i + 1)}
              className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-white/5 group"
              data-testid={`gallery-thumb-${i + 1}`}
              aria-label={`Open image ${i + 2} fullscreen`}
            >
              <img
                src={src}
                alt={`${project.title} — frame ${i + 2}`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                loading="lazy"
                draggable={false}
              />
            </button>
          ))}
        </div>
      )}

      {/* Prototype embed (UX/UI projects) */}
      {project.prototype && <PrototypeBlock prototype={project.prototype} />}

      {/* Description block */}
      <div className="mt-12 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
        <div className="md:col-span-4">
          <span className="block text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px] mb-3">
            Category
          </span>
          <p className="font-body font-bold text-white text-base md:text-lg">
            {categoryLabel}
          </p>

          <span className="block text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px] mt-7 mb-3">
            Year
          </span>
          <p className="font-body font-bold text-white text-base md:text-lg">
            {project.year}
          </p>

          {project.role && (
            <>
              <span className="block text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px] mt-7 mb-3">
                Role
              </span>
              <p className="font-body font-medium text-white/85 text-sm md:text-base leading-relaxed">
                {project.role}
              </p>
            </>
          )}

          {project.tools && (
            <>
              <span className="block text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px] mt-7 mb-3">
                Tools
              </span>
              <p className="font-body font-medium text-white/85 text-sm md:text-base leading-relaxed">
                {project.tools}
              </p>
            </>
          )}
        </div>

        <div className="md:col-span-8">
          <h1
            className="font-body font-extrabold text-white tracking-tight text-4xl md:text-6xl leading-[1.05]"
            data-testid={`project-title-${project.slug}`}
          >
            {project.title}
          </h1>

          {project.caseStudy?.length > 0 ? (
            <div
              className="mt-6 md:mt-10 space-y-8 md:space-y-10"
              data-testid={`project-casestudy-${project.slug}`}
            >
              {project.caseStudy.map((s, i) => (
                <section key={i} data-testid={`casestudy-section-${i}`}>
                  <h2 className="font-body font-extrabold text-white text-xl md:text-2xl tracking-tight">
                    {s.heading}
                  </h2>
                  <p className="mt-3 font-body font-medium text-white/85 text-base md:text-lg leading-[1.85]">
                    {s.body}
                  </p>
                </section>
              ))}
            </div>
          ) : (
            <div
              className="mt-6 md:mt-8 font-body font-medium text-white/85 text-base md:text-lg leading-[1.85] space-y-5"
              data-testid={`project-description-${project.slug}`}
            >
              {project.description.split(/\n\n+/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          )}
        </div>
      </div>
        </>
      )}

      {/* Next project */}
      {next && next.slug !== project.slug && (
        <div className="mt-16 md:mt-24 border-t border-white/15 pt-8 md:pt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <span className="text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px]">
            Next project
          </span>
          <Link
            to={`/projects/${next.slug}`}
            data-testid="next-project-link"
            className="group inline-flex items-center gap-3 font-body font-bold text-white text-2xl md:text-4xl tracking-tight hover:text-[#E5983D] transition-colors"
          >
            {next.title}
            <ArrowRight className="w-6 h-6 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      )}

      {lightboxIndex >= 0 && (
        <Lightbox
          images={lightboxImages}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prev}
          onNext={nextImg}
        />
      )}
    </section>
  );
}
